import { observer } from 'mobx-react-lite'
import { useFeatures } from '@/contexts/root-store-context'
import { useActivation } from '@/hooks/view-model-hooks'
import { JobWidget } from '@/features/jobs/job-widget/components/JobWidget'
import { JobWidgetHeader } from '@/features/jobs/job-widget/components/JobWidgetHeader'
import { JobWidgetDeadline } from '@/features/jobs/job-widget/components/JobWidgetDeadline'
import { JobWidgetStatus } from '@/features/jobs/job-widget/components/JobWidgetStatus'
import { Button } from '@/components/Button'
import { LabelChip } from '@/components/Chip'
import { links } from '@/utils/link'
import { progressionStepDescriptors } from '@/features/jobs/taxes/progression/progression-content'
import { useIsSkeleton } from '@/components/Skeleton'
import type { ConsultationStatus } from '@/features/jobs/consultations/Consultation'
import type { TimeRange } from '@/infra/time-range'
import { useProvideSupportWidgetHidden } from '@/features/support/components/ChatSupportSpaceManager'
import { Id } from '@/infra/id'

export default observer(function JobWidgetViewContainer() {
  const { internal } = useFeatures(({ jobs }) => ({
    internal: jobs.internal(),
  }))

  const vm = internal.jobWidgetVM
  const isSkeleton = useIsSkeleton(vm.isLoading)
  useActivation(vm)
  useProvideSupportWidgetHidden(true)

  const job = vm.jobWidget

  const { title: progressionTitle, description: progressionDescription } =
    progressionStepDescriptors[job.progressionStep]

  if (isSkeleton) {
    return (
      <JobWidget
        header={<JobWidgetHeader isSkeleton={isSkeleton} title="" />}
        button={
          <Button isSkeleton={isSkeleton} to="" label="" target="_blank" />
        }
      >
        <JobWidgetStatus
          isSkeleton={isSkeleton}
          title={job.name}
          icon="job-progression"
        />
      </JobWidget>
    )
  }

  const formatConsultationMessage = (
    consultationStatus: ConsultationStatus,
    bookedTime: TimeRange | undefined,
  ) => {
    if (
      consultationStatus === 'PendingCall' &&
      bookedTime?.isSameDay(new Date())
    ) {
      return `Your consultation is today at ${bookedTime.toStartingHourString()}`
    }

    if (consultationStatus === 'PendingCall' && bookedTime) {
      return `Your upcoming consultation is on ${bookedTime.toStartingDayString()} at ${bookedTime.toStartingHourString()}`
    }
  }

  const bookedTime = formatConsultationMessage(
    job.consultationStatus,
    job.consultationBookedTime,
  )

  const renderButton = (to: string, label: string) => (
    <Button to={to} target="_blank" label={label} />
  )

  if (job.id === Id.none || job.status === 'Unknown') {
    return (
      <JobWidget
        header={
          <JobWidgetHeader
            title="Are you ready to start filing with us?"
            subtitle="Fast, simple, and secure from start to finish. We guide you through every step so you can file with confidence."
          />
        }
        button={
          <Button
            to={links.startNewService()}
            target="_blank"
            label="Don't wait — start filing today"
          />
        }
      >
        <JobWidgetDeadline
          isSkeleton={isSkeleton}
          title="Tax deadline"
          subtitle="File now and avoid last minute stress."
        />
      </JobWidget>
    )
  }

  const viewJobButton = renderButton(links.jobDetails(job.id), 'View job')
  const underConstructionButton = renderButton(
    links.startNewService(),
    "Don't wait--start filing today",
  )
  const checkStatusButton = renderButton(
    links.jobDetails(job.id),
    'Check status',
  )

  switch (job.status) {
    case 'UnderConstruction':
      return job.intakeCompleted ? (
        <JobWidget
          header={
            <JobWidgetHeader
              title="Final step ahead!"
              subtitle="You've done the hard work, payment is the last step, before starting your filing. Pay now."
            />
          }
          button={underConstructionButton}
        >
          <JobWidgetStatus title={job.name} icon="job-progression" />

          <LabelChip text="Checkout" />
        </JobWidget>
      ) : (
        <JobWidget
          header={
            <JobWidgetHeader
              title="Finish answering your questions"
              subtitle="You're almost there! Just answer the last few questions to continue."
            />
          }
          button={underConstructionButton}
        >
          <JobWidgetDeadline
            isSkeleton={isSkeleton}
            title="Tax deadline"
            subtitle="File now and avoid last minute stress."
          />

          <LabelChip isSkeleton={isSkeleton} text="Under Construction" />
        </JobWidget>
      )
    case 'InfoGathering':
      return (
        <JobWidget
          header={
            <JobWidgetHeader
              title="Complete the list of requested documents"
              subtitle="Enter your personal information and upload all the required documents."
            />
          }
          button={renderButton(
            links.taxJobInfoGathering(job.id),
            'Upload Files',
          )}
        >
          <JobWidgetStatus title={job.name} icon="job-progression" />

          <LabelChip isSkeleton={isSkeleton} text="Info gathering" />
        </JobWidget>
      )
    case 'Unclaimed':
      return (
        <JobWidget
          header={
            <JobWidgetHeader
              title="Matching in progress"
              subtitle="We're connecting you to a Pro who will best assist with your inquiry"
            />
          }
          button={checkStatusButton}
        >
          <JobWidgetStatus title={job.name} icon="time" />

          <LabelChip isSkeleton={isSkeleton} text="Waiting pick up" />
        </JobWidget>
      )
    case 'Idle':
      return (
        <JobWidget
          header={
            <JobWidgetHeader
              title="Your job it's Idle"
              subtitle="Your job has been sitting idle for a while"
            />
          }
          button={viewJobButton}
        >
          <JobWidgetStatus title={job.name} icon="time" />

          <LabelChip isSkeleton={isSkeleton} text="Idle" />
        </JobWidget>
      )
    case 'Closed':
      return (
        <JobWidget
          header={
            <JobWidgetHeader
              title="Your tax return has been completed"
              subtitle="You will continue to have access to review the details of your tax return."
            />
          }
          button={renderButton(
            links.jobDetails(job.id),
            'Review final tax return',
          )}
        >
          <JobWidgetStatus title={job.name} icon="select-document" />

          <LabelChip
            isSkeleton={isSkeleton}
            text="Completed"
            variant="success"
          />
        </JobWidget>
      )
    case 'OnHold': {
      switch (job.scopeChangeStatus) {
        case 'PendingAdminApproval':
          return (
            <JobWidget
              header={
                <JobWidgetHeader
                  title="Your Pro is requesting a scope change"
                  subtitle="Your pro has identified that some additional services are required, a Customer Success Representative is reviewing the changes so the job can continue."
                />
              }
              button={viewJobButton}
            >
              <JobWidgetStatus title={job.name} icon="time" />

              <LabelChip text="Representative reviewing job" variant="danger" />
            </JobWidget>
          )
        case 'PendingClientApproval':
          return (
            <JobWidget
              header={
                <JobWidgetHeader
                  title="Your Pro is requesting a scope change"
                  subtitle="Your Pro has identified that some additional services are required, please approve the changes so that the job can continue"
                />
              }
              button={renderButton(
                links.jobDetails(job.id),
                'Approve or reject change',
              )}
            >
              <JobWidgetStatus title={job.name} icon="time" />

              <LabelChip text="Scope change requested" variant="danger" />
            </JobWidget>
          )
      }

      switch (job.progressionStep) {
        case 'OpenItems':
          return (
            <JobWidget
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              button={viewJobButton}
            >
              <JobWidgetStatus title={job.name} icon="time" />

              <LabelChip text="On Hold" variant="danger" />
            </JobWidget>
          )
        case 'DraftInReview':
          return (
            <JobWidget
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              button={renderButton(
                links.jobDetails(job.id),
                'Approve or reject draft',
              )}
            >
              <JobWidgetStatus title={job.name} icon="time" />

              <LabelChip text="Draft in review" />
            </JobWidget>
          )
        case 'ExtensionRejected':
          return (
            <JobWidget
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              button={checkStatusButton}
            >
              <JobWidgetStatus title={job.name} icon="update-job" />

              <LabelChip text="Extension accepted" />
            </JobWidget>
          )
        case 'ProgressionWaitingOnClient':
          return (
            <JobWidget
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              button={checkStatusButton}
            >
              <JobWidgetStatus title={job.name} icon="update-job" />

              <LabelChip text="Action requierd" variant="danger" />
            </JobWidget>
          )
      }

      return (
        <JobWidget
          header={
            <JobWidgetHeader
              title="Your job has been placed on hold"
              subtitle="A Customer Success Representative placed your job on hold. If you think this is a mistake, please reach out to Customer Success for assistance or chat with your Pro."
            />
          }
          button={viewJobButton}
        >
          <JobWidgetStatus title={job.name} icon="time" />

          <LabelChip text="On Hold" />
        </JobWidget>
      )
    }
    case 'Claimed': {
      switch (job.progressionStep) {
        case 'OpenItems':
          return (
            <JobWidget
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              bookedTime={bookedTime}
              button={viewJobButton}
            >
              <JobWidgetStatus title={job.name} icon="time" />

              <LabelChip text="On Hold" />
            </JobWidget>
          )
        case 'JobStarted':
          return (
            <JobWidget
              bookedTime={bookedTime}
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              button={renderButton(
                links.jobDetails(job.id),
                'Chat with the pro',
              )}
            >
              <JobWidgetStatus title={job.name} icon="time" />

              <LabelChip text="Job started" />
            </JobWidget>
          )
        case 'DraftApproved':
          return (
            <JobWidget
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              bookedTime={bookedTime}
              button={renderButton(
                links.jobDetails(job.id),
                'Sign Authorization',
              )}
            >
              <JobWidgetStatus title={job.name} icon="update-job" />

              <LabelChip text="Draft approved" />
            </JobWidget>
          )
        case 'DraftRejected':
          return (
            <JobWidget
              bookedTime={bookedTime}
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              button={checkStatusButton}
            >
              <JobWidgetStatus title={job.name} icon="update-job" />

              <LabelChip text="Changes requested" variant="danger" />
            </JobWidget>
          )
        case 'AuthorizationSigned':
          return (
            <JobWidget
              bookedTime={bookedTime}
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              button={viewJobButton}
            >
              <JobWidgetStatus title={job.name} icon="update-job" />

              <LabelChip text="Authorization received" />
            </JobWidget>
          )
        case 'ReturnFiled':
          return (
            <JobWidget
              bookedTime={bookedTime}
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              button={renderButton(
                links.jobDetails(job.id),
                'Review final tax return',
              )}
            >
              <JobWidgetStatus title={job.name} icon="select-document" />

              <LabelChip text="Return field" />
            </JobWidget>
          )
        case 'ExtensionInProgress':
          return (
            <JobWidget
              bookedTime={bookedTime}
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              button={checkStatusButton}
            >
              <JobWidgetStatus title={job.name} icon="update-job" />

              <LabelChip text="Extension in progress" />
            </JobWidget>
          )
        case 'ExtensionConfirmed':
          return (
            <JobWidget
              bookedTime={bookedTime}
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              button={checkStatusButton}
            >
              <JobWidgetStatus title={job.name} icon="update-job" />

              <LabelChip text="Waiting for confirmation" />
            </JobWidget>
          )
        case 'ReadyToFile':
          return (
            <JobWidget
              bookedTime={bookedTime}
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              button={viewJobButton}
            >
              <JobWidgetStatus title={job.name} icon="update-job" />

              <LabelChip text="Extension completed" />
            </JobWidget>
          )
      }

      return (
        <JobWidget
          header={
            <JobWidgetHeader
              title="Well done! The job has just started"
              subtitle="Talk to your Pro to have more information regarding next steps"
            />
          }
          bookedTime={bookedTime}
          button={viewJobButton}
        >
          <JobWidgetStatus title={job.name} icon="job-progression" />

          <LabelChip text="Assigned pro" />
        </JobWidget>
      )
    }
  }
})
